import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  Box,
  Paper,
  Typography,
  Tooltip,
  Fab,
  IconButton,
  Button,
  useTheme,
  alpha,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import ImageCarousel from '../common/ImageCarousel';
import { getCurrencySymbol } from '../../utils/currencyUtils';
import {
  MyLocation as MyLocationIcon,
  Layers as LayersIcon,
  Visibility as VisibilityIcon,
  Directions as DirectionsIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSearch } from '../../context/SearchContext';
import { useTranslation } from 'react-i18next';
import {
  createMapInstance,
  createPricePillMarkerElement,
  createUserLocationMarkerElement,
  getMapStyleUrl,
} from '../../utils/maplibreConfig';

const UPLOADS_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';
const normalizeImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${UPLOADS_BASE_URL}${url.startsWith('/') ? url : '/' + url}`;
};

const InteractiveSearchMap = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    results,
    selectedListing,
    selectListing,
    mapCenter,
    mapZoom,
    updateMapView,
    searchNearby,
    filters,
    productResults,
    searchType,
    loadMore,
    canLoadMore,
    isLoading,
  } = useSearch();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ── State ──────────────────────────────────────────────────────────────────
  const [userLocation, setUserLocation] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [mapStyle, setMapStyle] = useState('default');
  const [isLocating, setIsLocating] = useState(false);
  /** Pixel position of the selected marker — triggers mounting of the floating card */
  const [cardPixelPos, setCardPixelPos] = useState(null);
  const [lastResultsHash, setLastResultsHash] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const mapRef = useRef(null);
  /** Direct DOM handle for the floating card — updated every map-move frame */
  const cardRef = useRef(null);

  const handleLoadMore = useCallback(async () => {
    if (!canLoadMore || loadingMore || isLoading) return;
    setLoadingMore(true);
    try {
      await loadMore();
    } catch (error) {
      console.error('[InteractiveSearchMap] Error loading more:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [canLoadMore, loadMore, loadingMore, isLoading]);

  // ── Markers memo ───────────────────────────────────────────────────────────
  const markers = useMemo(() => {
    const showListings = searchType !== 'products';
    const showProducts = searchType !== 'listings';

    const listingMarkers = showListings
      ? (results.listings || [])
          .map(listing => {
            const lat = Number(listing.latitude);
            const lng = Number(listing.longitude);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
            return {
              ...listing,
              latitude: lat,
              longitude: lng,
              position: [lat, lng],
              isSelected: selectedListing?.id === listing.id && !selectedListing?.isProduct,
              isProduct: false,
              city: listing.city || '',
              region: listing.region || '',
              currency: listing.currency || 'EUR',
              priceLabel: (() => {
                const symbol = getCurrencySymbol(listing.currency || 'EUR');
                // Slot-based pricing
                const isSlot = listing.booking_mode === 'time_slot' || listing.booking_mode === 'time_slot_capacity';
                if (isSlot && listing.booking_settings) {
                  const slotPrice = listing.booking_settings.price_per_slot || listing.booking_settings.base_price;
                  if (slotPrice && slotPrice > 0) return `${Math.round(slotPrice)} ${symbol}`;
                }
                // Standard pricing
                const c = [
                  { v: listing.price_daily,   u: 'j' },
                  { v: listing.price_hourly,  u: 'h' },
                  { v: listing.price_weekly,  u: 's' },
                  { v: listing.price_monthly, u: 'm' },
                ].filter(p => p.v && p.v > 0);
                if (!c.length) return '—';
                const price = Math.round(c[0].v);
                const unit = t(`pricing:unit.${c[0].u}`);
                return `${price} ${symbol}`;
              })(),
            };
          })
          .filter(Boolean)
      : [];

    const productMarkers = showProducts
      ? (productResults?.products || [])
          .map(product => {
            const rawLat = product.twinListing?.latitude ?? product.pickup_location?.lat ?? null;
            const rawLng = product.twinListing?.longitude ?? product.pickup_location?.lng ?? null;
            const lat = Number(rawLat);
            const lng = Number(rawLng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
            const daily = (product.price_cents || 0) / 100;
            const productCurrency = product.currency || 'EUR';
            const symbol = getCurrencySymbol(productCurrency);
            const price = Math.round(daily);
            const unit = t('pricing:unit.j');
            
            // Extract city avec meilleurs fallbacks
            const extractCityFromAddress = (addr) => {
              if (!addr || typeof addr !== 'string') return '';
              const parts = addr.split(',').map(p => p.trim()).filter(Boolean);
              return parts.length >= 2 ? parts[parts.length - 2] : parts[0] || '';
            };
            const extractedCity = extractCityFromAddress(product.pickup_location?.address);
            
            return {
              ...product,
              latitude: lat,
              longitude: lng,
              position: [lat, lng],
              isSelected: selectedListing?.id === product.id && !!selectedListing?.isProduct,
              isProduct: true,
              city: product.twinListing?.city || product.pickup_location?.city || extractedCity || product.city || '',
              region: product.twinListing?.region || product.country_code || product.region || '',
              price_daily: daily,
              currency: productCurrency,
              priceLabel: daily > 0 ? `${price} ${symbol}` : '—',
            };
          })
          .filter(Boolean)
      : [];

    return [...listingMarkers, ...productMarkers];
  }, [results.listings, productResults, selectedListing, searchType]);

  // Mémoriser juste les positions pour le positionnement intelligent (sans isSelected)
  const positionsForFit = useMemo(
    () => markers
      .filter(m => Array.isArray(m.position))
      .map(m => ({ id: m.id, position: [Number(m.position[0]), Number(m.position[1])] }))
      .filter(p => Number.isFinite(p.position[0]) && Number.isFinite(p.position[1])),
    [markers],
  );

  // ── Force MapLibre to recalculate container dimensions ─────────────────
  useEffect(() => {
    if (!mapInstance) return;
    mapInstance.resize();
    const timers = [100, 300, 600].map(d => setTimeout(() => mapInstance.resize(), d));
    const onResize = () => mapInstance.resize();
    window.addEventListener('resize', onResize);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', onResize);
    };
  }, [mapInstance, results.listings, productResults?.products, searchType]);

  // ── Fit map to new search results (only when results genuinely change) ─────
  useEffect(() => {
    if (!mapInstance || positionsForFit.length === 0) return;
    const hash = positionsForFit.map(p => `${p.id}:${p.position}`).join('|');
    if (hash === lastResultsHash) return;
    setLastResultsHash(hash);

    if (positionsForFit.length === 1) {
      const lat = Number(positionsForFit[0].position[0]);
      const lng = Number(positionsForFit[0].position[1]);
      if (isFinite(lat) && isFinite(lng)) {
        mapInstance.flyTo({ center: [lng, lat], zoom: 15 });
      }
    } else {
      const lats = positionsForFit.map(p => p.position[0]);
      const lngs = positionsForFit.map(p => p.position[1]);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);

      if (!Number.isFinite(minLat) || !Number.isFinite(maxLat) || !Number.isFinite(minLng) || !Number.isFinite(maxLng)) return;

      try {
        // MapLibre fitBounds : [[minLng, minLat], [maxLng, maxLat]]
        mapInstance.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
          padding: 28,
          animate: true,
          duration: 800,
        });
      } catch (_) {}
    }
  }, [mapInstance, positionsForFit, lastResultsHash]);

  // ── Show/hide the floating card when selection changes ─────────────────────
  useEffect(() => {
    const lat = Number(selectedListing?.latitude);
    const lng = Number(selectedListing?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setCardPixelPos(null);
      return;
    }
    if (!mapInstance) return;

    let cancelled = false;

    const computePos = () => {
      if (cancelled) return;
      // MapLibre : map.project([lng, lat]) → {x, y}
      const p = mapInstance.project([lng, lat]);
      setCardPixelPos({ x: p.x, y: p.y });
    };

    // Pan gently only when the marker is outside the visible area
    const bounds = mapInstance.getBounds();
    if (!bounds.contains([lng, lat])) {
      mapInstance.panTo([lng, lat]);
      mapInstance.once('moveend', computePos);
    } else {
      computePos();
    }

    return () => {
      cancelled = true;
      mapInstance.off('moveend', computePos);
    };
  }, [selectedListing, mapInstance]);

  /**
   * Keep the floating card glued to its marker on every pan/zoom frame.
   * Uses direct DOM mutation — no React re-renders per animation frame.
   */
  useEffect(() => {
    const lat = Number(selectedListing?.latitude);
    const lng = Number(selectedListing?.longitude);
    if (!mapInstance || !Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const onMove = () => {
      if (!cardRef.current) return;
      // MapLibre : map.project([lng, lat])
      const p = mapInstance.project([lng, lat]);
      cardRef.current.style.left = `${p.x}px`;
      cardRef.current.style.top  = `${p.y}px`;
    };

    mapInstance.on('move', onMove);
    return () => mapInstance.off('move', onMove);
  }, [mapInstance, selectedListing]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleMarkerClick = useCallback((listing) => {
    selectListing(listing, 'map');
    // cardPixelPos will be set by the selectedListing effect above
  }, [selectListing]);

  const handleCloseCard = useCallback(() => {
    selectListing(null);
    setCardPixelPos(null);
  }, [selectListing]);

  const handleViewListing = useCallback((listing) => {
    const path = listing.isProduct 
      ? `/product/${listing.id}/order`
      : `/view-listing/${listing.id}`;
    window.open(window.location.origin + path, '_blank');
  }, []);

  const handleMapMove = useCallback(({ center, zoom }) => {
    updateMapView([center.lat, center.lng], zoom);
  }, [updateMapView]);

  const handleMapClick = useCallback(() => {
    if (selectedListing) {
      selectListing(null);
      setCardPixelPos(null);
    }
  }, [selectedListing, selectListing]);

  const handleGetDirections = useCallback((listing) => {
    if (!listing.latitude || !listing.longitude) return;
    const dest  = `${listing.latitude},${listing.longitude}`;
    const label = encodeURIComponent(listing.title || 'Destination');
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      const fallback = `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
      const native   = /iPhone|iPad|iPod/i.test(navigator.userAgent)
        ? `maps://maps.apple.com/?daddr=${dest}`
        : `geo:${dest}?q=${dest}`;
      const frame = document.createElement('iframe');
      frame.style.display = 'none';
      frame.src = native;
      document.body.appendChild(frame);
      setTimeout(() => { document.body.removeChild(frame); window.open(fallback, '_blank'); }, 900);
    } else {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${dest}&destination_place_id=${label}`,
        '_blank',
      );
    }
  }, []);

  const getUserLocation = useCallback(() => {
    if (!navigator.geolocation) { alert(t('map.geolocation.notSupported')); return; }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        setUserLocation(loc);
        // MapLibre : flyTo center = [lng, lat]
        mapInstance?.flyTo({ center: [loc.longitude, loc.latitude], zoom: 15 });
        updateMapView([loc.latitude, loc.longitude], 15);
        searchNearby(loc, filters.radius || 10);
        setLastResultsHash('');
        setIsLocating(false);
      },
      (err) => { console.error('Geolocation:', err); alert(t('map.geolocation.error')); setIsLocating(false); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 },
    );
  }, [t, updateMapView, searchNearby, filters.radius, mapInstance]);

  const toggleMapStyle = () => {
    const styles = ['default', 'satellite', 'terrain'];
    setMapStyle(s => styles[(styles.indexOf(s) + 1) % styles.length]);
  };

  const getMapStyle = useCallback(() => {
    return getMapStyleUrl(mapStyle);
  }, [mapStyle]);

  // ── Map container ref ──────────────────────────────────────────────────────
  const mapContainerRef = useRef(null);
  // mapRef holds the maplibregl.Map instance (read-only from state for effects)

  // ── MapLibre map initialization ────────────────────────────────────────────
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const initialCenter = Array.isArray(mapCenter) && mapCenter.length === 2
      ? [mapCenter[1], mapCenter[0]] // [lng, lat]
      : [2.3522, 48.8566];

    const map = createMapInstance({
      container: mapContainerRef.current,
      styleVariant: mapStyle,
      center: initialCenter,
      zoom: mapZoom || 13,
      pitch: 24,
    });

    map.on('moveend', () => {
      const center = map.getCenter();
      updateMapView([center.lat, center.lng], map.getZoom());
    });

    map.on('click', () => {
      if (selectedListingRef.current) {
        selectListing(null);
        setCardPixelPos(null);
      }
    });

    mapRef.current = map;
    setMapInstance(map);

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ref to avoid stale closure in map click handler
  const selectedListingRef = useRef(selectedListing);
  useEffect(() => { selectedListingRef.current = selectedListing; }, [selectedListing]);

  // ── Update map style when toggled ─────────────────────────────────────────
  useEffect(() => {
    if (!mapInstance) return;
    mapInstance.setStyle(getMapStyle());
  }, [mapStyle, mapInstance, getMapStyle]);

  // ── Manage price-pill markers (MapLibre Marker objects) ───────────────────
  const markerObjectsRef = useRef({}); // id -> { marker, el }

  useEffect(() => {
    const map = mapInstance;
    if (!map) return;

    const currentIds = new Set(markers.map(m => String(m.id)));

    // Remove stale markers
    Object.keys(markerObjectsRef.current).forEach(id => {
      if (!currentIds.has(id)) {
        markerObjectsRef.current[id].marker.remove();
        delete markerObjectsRef.current[id];
      }
    });

    // Add or update markers
    markers.forEach(listing => {
      const id = String(listing.id);
      const [lat, lng] = [Number(listing.latitude), Number(listing.longitude)];
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      if (markerObjectsRef.current[id]) {
        // Update position and icon
        const { marker, el } = markerObjectsRef.current[id];
        marker.setLngLat([lng, lat]);
        // Update selected style
        const isSelected = listing.isSelected;
        el.style.background = isSelected ? '#111827' : '#ffffff';
        el.style.color = isSelected ? '#ffffff' : '#111827';
        el.style.boxShadow = isSelected ? '0 4px 18px rgba(0,0,0,0.30)' : '0 2px 8px rgba(0,0,0,0.16)';
        el.style.border = isSelected ? 'none' : '1.5px solid rgba(0,0,0,0.11)';

        // Bring selected marker to front so it isn't hidden by overlapping neighbours
        const markerContainer = marker.getElement();
        if (markerContainer) {
          if (isSelected) {
            markerContainer.style.zIndex = 100;
            if (markerContainer.parentNode) {
              markerContainer.parentNode.appendChild(markerContainer);
            }
          } else {
            markerContainer.style.zIndex = '';
          }
        }
      } else {
        const el = createPricePillMarkerElement(listing.priceLabel, listing.isSelected);
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          handleMarkerClick(listing);
        });
        const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([lng, lat])
          .addTo(map);
        markerObjectsRef.current[id] = { marker, el };

        // Bring selected marker to front on initial mount too
        if (listing.isSelected) {
          const markerContainer = marker.getElement();
          if (markerContainer) {
            markerContainer.style.zIndex = 100;
            if (markerContainer.parentNode) {
              markerContainer.parentNode.appendChild(markerContainer);
            }
          }
        }
      }
    });
  }, [markers, mapInstance]);

  // ── User location dot marker ───────────────────────────────────────────────
  const userDotRef = useRef(null);
  useEffect(() => {
    if (!mapInstance || !userLocation) return;
    if (userDotRef.current) {
      userDotRef.current.setLngLat([userLocation.longitude, userLocation.latitude]);
      return;
    }
    const el = createUserLocationMarkerElement();
    userDotRef.current = new maplibregl.Marker({ element: el, anchor: 'center' })
      .setLngLat([userLocation.longitude, userLocation.latitude])
      .addTo(mapInstance);
  }, [userLocation, mapInstance]);

  // Enrichir selectedListing avec les données des marqueurs (pour les données complètes city/region)
  const enrichedSelectedListing = useMemo(() => {
    if (!selectedListing) return null;
    // Chercher le marqueur correspondant
    const matchingMarker = markers.find(m => m.id === selectedListing.id && m.isProduct === (selectedListing.isProduct || false));
    // Fusionner les données enrichies du marqueur avec selectedListing
    if (matchingMarker) {
      return { ...selectedListing, ...matchingMarker };
    }
    return selectedListing;
  }, [selectedListing, markers]);

  // Préparer les images du mini-card (même logique que SearchResultCard)
  const cardImages = useMemo(() => {
    if (!enrichedSelectedListing) return [];
    const isProduct = enrichedSelectedListing.isProduct || false;
    const mainImage = normalizeImageUrl(enrichedSelectedListing.mainImageUrl) || null;
    let additionalImages = [];

    if (isProduct) {
      if (enrichedSelectedListing.images && Array.isArray(enrichedSelectedListing.images) && enrichedSelectedListing.images.length > 0) {
        const firstImg = enrichedSelectedListing.images[0];
        if (typeof firstImg === 'string') {
          additionalImages = enrichedSelectedListing.images
            .map(url => normalizeImageUrl(url))
            .filter(url => url && url !== mainImage);
        } else if (firstImg && typeof firstImg === 'object' && firstImg.image_url) {
          additionalImages = enrichedSelectedListing.images
            .map(img => normalizeImageUrl(img.image_url))
            .filter(url => url && url !== mainImage);
        }
      }
    } else {
      if (enrichedSelectedListing.images && Array.isArray(enrichedSelectedListing.images) && enrichedSelectedListing.images.length > 0) {
        additionalImages = enrichedSelectedListing.images
          .map(img => normalizeImageUrl(img.image_url))
          .filter(url => url && url !== mainImage);
      } else if (enrichedSelectedListing.imageUrls && Array.isArray(enrichedSelectedListing.imageUrls) && enrichedSelectedListing.imageUrls.length > 0) {
        additionalImages = enrichedSelectedListing.imageUrls
          .map(url => normalizeImageUrl(url))
          .filter(url => url && url !== mainImage);
      }
    }

    let images = Array.from(new Set([
      mainImage,
      ...additionalImages
    ].filter(Boolean)));

    // Sort by order_index for listings
    if (!isProduct && enrichedSelectedListing.images && Array.isArray(enrichedSelectedListing.images) && enrichedSelectedListing.images.length > 0) {
      try {
        const orderMap = new Map();
        enrichedSelectedListing.images.forEach(img => {
          if (img.image_url && img.order_index !== undefined) {
            orderMap.set(normalizeImageUrl(img.image_url), img.order_index);
          }
        });
        if (orderMap.size > 0) {
          images.sort((a, b) => {
            const orderA = orderMap.has(a) ? orderMap.get(a) : 999;
            const orderB = orderMap.has(b) ? orderMap.get(b) : 999;
            return orderA - orderB;
          });
        }
      } catch (error) {
        console.warn('Erreur lors du tri des images par ordre:', error);
      }
    }

    return images;
  }, [enrichedSelectedListing]);

  const formatPrice = (listing) => {
    const symbol = getCurrencySymbol(listing?.currency || 'EUR');
    // Slot-based pricing
    const isSlot = listing?.booking_mode === 'time_slot' || listing?.booking_mode === 'time_slot_capacity';
    if (isSlot && listing?.booking_settings) {
      const slotPrice = listing.booking_settings.price_per_slot || listing.booking_settings.base_price;
      if (slotPrice && slotPrice > 0) return `${Math.round(slotPrice)} ${symbol}`;
    }
    // Standard pricing - Priorité: daily > weekly > monthly > hourly
    const prices = [
      { value: listing?.price_daily,   unit: 'j' },
      { value: listing?.price_weekly,  unit: 's' },
      { value: listing?.price_monthly, unit: 'm' },
      { value: listing?.price_hourly,  unit: 'h' },
    ].filter(p => p.value && p.value > 0);
    if (!prices.length) return t('listing.price.contact');
    const price = Math.round(prices[0].value);
    const unit = t(`pricing:unit.${prices[0].unit}`);
    return `${price} ${symbol}`;
  };

  const formatLocation = (listing) => {
    const parts = [listing?.city, listing?.region].filter(Boolean);
    return parts.join(', ') || t('listing.location.unknown');
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        position: 'relative',
      }}
    >
      {/* ── CSS injected once for card animation & pill resets ─────────────── */}
      <style>{`
        @keyframes cardPop {
          from { opacity: 0; transform: translate(-50%, calc(-100% - 16px)) scale(0.84); }
          to   { opacity: 1; transform: translate(-50%, calc(-100% - 16px)) scale(1);    }
        }
        .price-pill-marker { cursor: pointer !important; }
        .maplibregl-marker { pointer-events: auto; }
      `}</style>

      {/* ── MapLibre GL JS container ─────────────────────────────────────── */}
      <div
        ref={mapContainerRef}
        style={{ height: '100%', width: '100%', display: 'block' }}
      />

      {/* ── Floating card overlay ─────────────────────────────────────────────
          Plain React element positioned absolutely over the map container.
          Completely outside MapLibre's render pipeline → zero jitter on zoom/pan.
          Initial position from cardPixelPos state; subsequent positions updated
          via direct DOM mutation in the map.on('move') handler — no re-renders. */}
      {enrichedSelectedListing && cardPixelPos && (
        <Box
          key={enrichedSelectedListing.id}   // remount when switching listings → re-plays animation
          ref={cardRef}
          sx={{
            position: 'absolute',
            left: cardPixelPos.x,
            top: cardPixelPos.y,
            transform: 'translate(-50%, calc(-100% - 16px))',
            zIndex: 1002,
            pointerEvents: 'auto',
            animation: 'cardPop 0.22s cubic-bezier(0.34, 1.56, 0.64, 1) both',
          }}
        >
          <Paper
            elevation={16}
            sx={{
              width: 248,
              borderRadius: 3,
              overflow: 'hidden',
              position: 'relative',
              background: theme.palette.background.paper,
            }}
          >
            {/* Close button */}
            <IconButton
              size="small"
              onClick={handleCloseCard}
              sx={{
                position: 'absolute',
                top: 7,
                right: 7,
                zIndex: 10,
                width: 26,
                height: 26,
                backgroundColor: alpha(theme.palette.background.paper, 0.88),
                backdropFilter: 'blur(4px)',
                '&:hover': { backgroundColor: alpha(theme.palette.error.light, 0.18) },
              }}
            >
              <CloseIcon sx={{ fontSize: '0.8rem' }} />
            </IconButton>

            {/* Listing / Product image carousel */}
            <Box sx={{ width: '100%', height: 138 }}>
              <ImageCarousel
                images={cardImages}
                alt={enrichedSelectedListing.title}
                width="100%"
                height={138}
                showControls={cardImages.length > 1}
                showIndicators={cardImages.length > 1}
                showCounter={false}
                autoPlay={false}
              />
            </Box>

            <Box sx={{ p: 1.5 }}>
              {/* Title + price inline */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.4 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  noWrap
                  sx={{ pr: 1, lineHeight: 1.3, flex: 1, minWidth: 0 }}
                >
                  {enrichedSelectedListing.title}
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="primary.main"
                  fontWeight={800}
                  noWrap
                  sx={{ lineHeight: 1.2, flexShrink: 0 }}
                >
                  {formatPrice(enrichedSelectedListing)}
                </Typography>
              </Box>

              {/* Location */}
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                display="block"
                sx={{ mb: 1.25 }}
              >
                {formatLocation(enrichedSelectedListing)}
              </Typography>

              {/* Action row */}
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  onClick={() => handleViewListing(enrichedSelectedListing)}
                  startIcon={<VisibilityIcon sx={{ fontSize: '0.85rem' }} />}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    py: 0.8,
                    boxShadow: 'none',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    '&:hover': {
                      boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.38)}`,
                    },
                  }}
                >
                  {t('listing.actions.view')}
                </Button>

                <Tooltip title={t('listing.actions.getDirections')} placement="top">
                  <IconButton
                    size="small"
                    onClick={() => handleGetDirections(enrichedSelectedListing)}
                    sx={{
                      flexShrink: 0,
                      border: `1.5px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      color: 'text.secondary',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        color: 'primary.main',
                        backgroundColor: alpha(theme.palette.primary.main, 0.06),
                      },
                    }}
                  >
                    <DirectionsIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Paper>

          {/* Downward stem connecting card to the marker */}
          <Box
            sx={{
              width: 0,
              height: 0,
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              borderTop: `10px solid ${theme.palette.background.paper}`,
              mx: 'auto',
              filter: 'drop-shadow(0 4px 3px rgba(0,0,0,0.14))',
            }}
          />
        </Box>
      )}

      {/* ── Map controls (top-right) ──────────────────────────────────────── */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Tooltip title={t('map.findMyLocation')}>
          <Fab
            size="small"
            onClick={getUserLocation}
            disabled={isLocating}
            sx={{
              backgroundColor: 'background.paper',
              color: 'primary.main',
              boxShadow: theme.shadows[4],
              '&:hover': { backgroundColor: 'primary.main', color: 'primary.contrastText' },
            }}
          >
            <MyLocationIcon />
          </Fab>
        </Tooltip>

        <Tooltip title={t('map.changeStyle')}>
          <Fab
            size="small"
            onClick={toggleMapStyle}
            sx={{
              backgroundColor: 'background.paper',
              color: 'text.primary',
              boxShadow: theme.shadows[4],
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <LayersIcon />
          </Fab>
        </Tooltip>
      </Box>

      {/* ── Results count badge (bottom-left) ────────────────────────────── */}
      {markers.length > 0 && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            zIndex: 1000,
            px: 2,
            py: 1,
            borderRadius: 2,
            background: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {t('map.resultsCount', { count: markers.length })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('map.zoomForMore')}
          </Typography>
        </Paper>
      )}

      {/* ── Geolocation busy indicator ────────────────────────────────────── */}
      {isLocating && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1001,
            backgroundColor: alpha(theme.palette.background.paper, 0.92),
            p: 2,
            borderRadius: 2,
            textAlign: 'center',
            backdropFilter: 'blur(6px)',
          }}
        >
          <Typography variant="body2">{t('map.locating')}</Typography>
        </Box>
      )}

      {/* ── Load More (mobile only) ────────────────────────────────────────── */}
      {isMobile && canLoadMore && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={handleLoadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              borderRadius: 6,
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              py: 0.8,
              boxShadow: theme.shadows[6],
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              '&:hover': {
                boxShadow: theme.shadows[8],
              },
            }}
          >
            {loadingMore ? t('search.loading.more', 'Chargement...') : t('search.load_more', 'Charger plus')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default InteractiveSearchMap;
